import React, { useEffect, useState } from "react";
import { Save, ExternalLink } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#00E5FF]";

const FIELDS = [
  ["name", "Public operator / company name", "text"],
  ["tagline", "Tagline (one line)", "text"],
  ["logo", "Logo / photo URL", "text"],
  ["website", "Website URL", "text"],
  ["instagram", "Instagram (@handle or URL)", "text"],
  ["specialties", "Specialties — one per line", "list"],
  ["bio", "About you / your company", "textarea"],
];

export default function OperatorProfileEditor() {
  const [f, setF] = useState({});
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/my/operator-profile").then((r) => {
      if (r.data) {
        setSlug(r.data.slug);
        setF({ ...r.data, specialties: (r.data.specialties || []).join("\n") });
      }
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!f.name) { toast.error("Add your public name"); return; }
    setBusy(true);
    const payload = { ...f, specialties: (f.specialties || "").split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      const { data } = await api.post("/my/operator-profile", payload);
      setSlug(data.slug);
      toast.success("Profile saved — it's live");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-bold text-2xl text-white">Your Public Profile</h2>
        {slug && <a href={`/operators/${slug}`} target="_blank" rel="noreferrer" data-testid="view-public-profile" className="text-[#00E5FF] text-sm inline-flex items-center gap-1 hover:underline">View public page <ExternalLink size={13} /></a>}
      </div>
      <p className="text-neutral-500 text-sm mb-5">This is how visitors discover and trust you. It shows on your operator page alongside all your tours.</p>
      <form onSubmit={save} className="border border-white/10 bg-[#141414] p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl">
        {FIELDS.map(([k, label, type]) => (
          <div key={k} className={type === "textarea" || type === "list" ? "sm:col-span-2" : ""}>
            <label className="text-neutral-400 text-xs">{label}</label>
            {type === "textarea" || type === "list"
              ? <textarea rows={type === "list" ? 3 : 4} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}`} className={`mt-1 ${inputCls}`} />
              : <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}`} className={`mt-1 ${inputCls}`} />}
          </div>
        ))}
        <Button type="submit" disabled={busy} data-testid="profile-save" className="bg-[#FF0055] hover:bg-[#ff2d74] rounded-none w-fit sm:col-span-2"><Save size={15} /> Save Profile</Button>
      </form>
    </div>
  );
}
